/**
 * Bill detail sticky rail — `prototype/purchases/bill-detail.html` ▸ `.rail`.
 *
 * Cards:
 *   1. Founder approval / Next action  (amber tint when approval_pending)
 *   2. Approval routing widget         (.routing with .routing-marker + .routing-content)
 *   3. Money                            (.row.grand + .row.itc)
 *   4. Vendor                           (.who/.ava/.copy + .field)
 *   5. Files                            (BillAttachmentsCard)
 *   6. Internal note (conditional)      (.rail-note with .stamp)
 */
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  CreditCard,
  Lock,
  Pencil,
  RotateCcw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { initials } from "../../../lib/initials.ts";
import { formatDate, formatDateTime, paiseToInrExact } from "../../../lib/format.ts";
import { BillAttachmentsCard } from "../components/bill-attachments-card.tsx";
import type { BillStatus } from "../list/index.ts";

/** Inputs and callbacks for the bill sticky right rail. */
export interface BillRailProps {
  bill: {
    id: string;
    vendorId: string;
    vendorName: string;
    vendorGstin: string | null;
    vendorCity: string | null;
    vendorState: string | null;
    status: BillStatus;
    subtotalPaise: string;
    taxPaise: string;
    totalPaise: string;
    internalNote: string | null;
    approver: string | null;
    createdAt: string;
    createdByActorId: string;
  };
  isFounder: boolean;
  onEdit: () => void;
  onTransition: (status: BillStatus) => void;
  pending?: boolean;
}

/** Render approval, routing, money, vendor, files, and note cards for a bill. */
export function BillRail({ bill, isFounder, onEdit, onTransition, pending }: BillRailProps) {
  const isApprovalPending = bill.status === "approval_pending";
  const isReadyToPay = bill.status === "ready_to_pay";
  const isPaid = bill.status === "paid";
  const isVoid = bill.status === "void";
  const isDraft = bill.status === "draft";
  const isReview = bill.status === "review";

  // Routing state — three steps: operator → founder → payment.
  const operatorStep = isDraft || isReview ? "now" : "done";
  const founderStep = isApprovalPending ? "now" : isReadyToPay || isPaid ? "done" : "todo";
  const paymentStep = isPaid ? "done" : isReadyToPay ? "now" : "todo";

  return (
    <aside className="rail" aria-label="Quick actions and summary">
      {/* Next action / Founder approval */}
      <section className="rail-card rail-next">
        <div className="head">
          <h4>{isApprovalPending ? "Founder approval" : "Next action"}</h4>
        </div>
        <div className="body">
          <p className="pri-line">{nextActionCopy(bill.status, bill.createdAt)}</p>
          {isApprovalPending ? (
            <div className="otp-hint" role="status">
              <Lock size={12} aria-hidden />
              OTP step-up required · code sent on Approve
            </div>
          ) : null}
          <div className="actions">
            {isApprovalPending ? (
              <>
                <button
                  type="button"
                  className="btn primary"
                  disabled={!isFounder || pending}
                  onClick={() => onTransition("ready_to_pay")}
                >
                  <ShieldCheck size={12} />
                  Approve · OTP
                </button>
                <button
                  type="button"
                  className="btn secondary"
                  disabled={pending}
                  onClick={() => onTransition("review")}
                >
                  <RotateCcw size={12} />
                  Hold &amp; ask vendor
                </button>
                <button
                  type="button"
                  className="btn danger"
                  disabled={pending}
                  onClick={() => onTransition("void")}
                >
                  <XCircle size={12} />
                  Reject
                </button>
              </>
            ) : isDraft ? (
              <button type="button" className="btn primary" onClick={onEdit}>
                <Pencil size={12} />
                Continue editing
              </button>
            ) : isReview ? (
              <button
                type="button"
                className="btn primary"
                onClick={() => onTransition("approval_pending")}
                disabled={pending}
              >
                <ShieldCheck size={12} />
                Submit for approval
              </button>
            ) : isReadyToPay ? // Primary "Record payment" CTA lives in the header + footer
            // bands per the bill-detail layout — see `bill-detail-header.tsx`
            // and `bill-detail-foot.tsx`. The rail used to hold a disabled
            // placeholder; removed to avoid three competing entry points.
            null : null}
          </div>
        </div>
      </section>

      {/* Approval routing */}
      <section className="rail-card">
        <div className="head">
          <h4>Approval routing</h4>
        </div>
        <div className="body">
          <ol className="routing">
            {/* "who" line uses a generic label until the bill payload
                carries the operator's display name (V1.x — needs a join
                from `created_by_actor_id` to `users.name`). Showing the
                hardcoded "Priya Mehta" would have read as fake on every
                bill the operator opened. */}
            <RoutingStep
              who="Operator"
              role="Captured + matched"
              when={
                operatorStep === "done"
                  ? formatDateTime(bill.createdAt)
                  : operatorStep === "now"
                    ? "In progress"
                    : "Pending"
              }
              status={operatorStep}
              icon={<Check size={9} strokeWidth={2.4} />}
            />
            <RoutingStep
              who={bill.approver ?? "Founder"}
              role="Founder · OTP step-up"
              when={
                founderStep === "done" ? "Approved" : founderStep === "now" ? "Awaiting" : "Pending"
              }
              status={founderStep}
              icon={<ShieldCheck size={9} strokeWidth={2.4} />}
            />
            <RoutingStep
              who="Payment recorded"
              role="Bank · NEFT / RTGS"
              when={
                paymentStep === "done"
                  ? "Paid"
                  : paymentStep === "now"
                    ? "Pay via your bank app"
                    : "After approval"
              }
              status={paymentStep}
              icon={<CreditCard size={9} strokeWidth={2.4} />}
            />
          </ol>
        </div>
      </section>

      {/* Money */}
      <section className="rail-card rail-money">
        <div className="head">
          <h4>Money</h4>
        </div>
        <div className="body">
          <div className="row">
            <span>Subtotal</span>
            <span className="v">{paiseToInrExact(bill.subtotalPaise)}</span>
          </div>
          <div className="row">
            <span>GST</span>
            <span className="v">{paiseToInrExact(bill.taxPaise)}</span>
          </div>
          <div className="row grand">
            <span>Bill total</span>
            <span className="v">{paiseToInrExact(bill.totalPaise)}</span>
          </div>
          <div className="row itc">
            <span>GST input credit</span>
            <span className="v">+ {paiseToInrExact(bill.taxPaise)}</span>
          </div>
        </div>
      </section>

      {/* Vendor */}
      <section className="rail-card rail-party">
        <div className="head">
          <h4>Vendor</h4>
          <Link
            to="/procurement/vendors/$vendorId"
            params={{ vendorId: bill.vendorId }}
            className="open-link"
            aria-label="Open vendor detail"
          >
            Open <ArrowRight size={11} />
          </Link>
        </div>
        <div className="body">
          <div className="who">
            <div className="ava" aria-hidden>
              {initials(bill.vendorName)}
            </div>
            <div className="copy">
              <Link to="/procurement/vendors/$vendorId" params={{ vendorId: bill.vendorId }} className="name">
                {bill.vendorName}
              </Link>
              <div className="meta">{bill.vendorGstin ?? "Unregistered"}</div>
            </div>
          </div>
          <div className="field">
            <span className="k">Location</span>
            <span className="v">
              {[bill.vendorCity, bill.vendorState].filter(Boolean).join(", ") || "Not on file"}
            </span>
          </div>
          <div className="field">
            <span className="k">Captured</span>
            <span className="v">{formatDate(bill.createdAt)}</span>
          </div>
        </div>
      </section>

      {/* Files */}
      <section className="rail-card rail-files">
        <div className="head">
          <h4>Files</h4>
        </div>
        <div className="body">
          <BillAttachmentsCard billId={bill.id} />
        </div>
      </section>

      {/* Internal note (conditional) */}
      {bill.internalNote ? (
        <section className="rail-card rail-note">
          <div className="head">
            <h4>Internal note</h4>
          </div>
          <div className="body">
            <div className="stamp">Captured {formatDate(bill.createdAt)}</div>
            <p style={{ margin: 0 }}>{bill.internalNote}</p>
          </div>
        </section>
      ) : null}

      {/* Voided state — small banner */}
      {isVoid ? (
        <section className="rail-card">
          <div className="head">
            <h4>Voided</h4>
          </div>
          <div className="body" style={{ fontSize: 12, color: "var(--muted)" }}>
            This bill is voided on {formatDate(bill.createdAt)}. Historical record only — no further
            payment actions are possible.
          </div>
        </section>
      ) : null}
    </aside>
  );
}

interface RoutingStepProps {
  who: string;
  role: string;
  when: string;
  status: "done" | "now" | "todo";
  icon: React.ReactNode;
}

function RoutingStep({ who, role, when, status, icon }: RoutingStepProps) {
  return (
    <li className={`routing-step ${status}`}>
      <span className="routing-marker">{icon}</span>
      <div className="routing-content">
        <div className="who">{who}</div>
        <div className="role">{role}</div>
        <div className="when">{when}</div>
      </div>
    </li>
  );
}

function nextActionCopy(status: BillStatus, createdAt: string): string {
  if (status === "draft")
    return "Finish capturing this bill — fill remaining fields and submit for approval.";
  if (status === "review") return "Three-way match in progress. Submit when PO + GRN reconcile.";
  if (status === "approval_pending")
    return "Routed for founder approval. OTP step-up required — code sent on Approve.";
  if (status === "ready_to_pay") return "Approved. Pay via your bank app, then mark paid here.";
  if (status === "paid") return `Paid on ${formatDate(createdAt)}. ITC claimed in GSTR-3B.`;
  return `Voided on ${formatDate(createdAt)}. Historical record only.`;
}
